"use client"
import { useFormContext } from "react-hook-form"
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"

interface MultiInputFormFieldProps {
  names: [string, string, string]
  type: [string, string, string]
  label: string
  placeholders?: [string, string, string]
  required?: boolean
}

export function MultiInputFormField({
  names,
  type,
  label,
  placeholders = ["", "", ""],
  required = false,
  layout = 'horizontal'
}: MultiInputFormFieldProps & { layout?: 'horizontal' | 'vertical' }) {
  const form = useFormContext()

  const formErrors = form.formState.errors
  const hasErrors = Object.keys(formErrors).filter(n => names.includes(n)).length > 0

  // Create a unified error message
  const getErrorMessages = () => {
    const messages: string[] = []
    Object.entries(formErrors).forEach(([field, error]) => {
      if (names.includes(field) && error?.message) {
        messages.push(error.message as string)
      }
    })
    return messages
  }

  if (layout === 'vertical') {
    return (
      <div className="space-y-2">
        <FormLabel className={`${hasErrors ? 'text-red-500' : ''}`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </FormLabel>

        <div className="flex w-full gap-0">
          <div className="flex-1">
            <FormField
              control={form.control}
              name={names[0]}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                    <Input
                      placeholder={placeholders[0]}
                      type={type[0]}
                      step="any"
                      min="0"
                      className="rounded-r-none rounded-l-md focus:z-10 relative"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex-1">
            <FormField
              control={form.control}
              name={names[1]}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                  <Input
                    placeholder={placeholders[1]}
                    type={type[1]}
                    step="any"
                    min="0"
                    className="rounded-none focus:z-10 relative -ms-px"
                    {...field}
                    value={field.value ?? ""}
                  />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex-1">
            <FormField
              control={form.control}
              name={names[2]}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                  <Input
                    placeholder={placeholders[2]}
                    type={type[2]}
                    step="any"
                    min="0"
                    className="rounded-l-none rounded-r-md focus:z-10 relative -ms-px"
                    {...field}
                    value={field.value ?? ""}
                  />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        {hasErrors && (
          <FormMessage>
            {getErrorMessages().pop()}
          </FormMessage>
        )}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0">
      <FormLabel className={`col-span-2 text-right ${hasErrors ? 'text-red-500' : ''}`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </FormLabel>

      <div className="flex col-span-4 flex-col sm:flex-row w-full gap-0">
        <div className="flex-1">
          <FormField
            control={form.control}
            name={names[0]}
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormControl>
                  <Input
                    placeholder={placeholders[0]}
                    type={type[0]}
                    step="any"
                    min="0"
                    className="rounded-r-none rounded-l-md focus:z-10 relative"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex-1">
          <FormField
            control={form.control}
            name={names[1]}
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormControl>
                  <Input
                    placeholder={placeholders[1]}
                    type={type[1]}
                    step="any"
                    min="0"
                    className="rounded-none focus:z-10 relative -ms-px"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex-1">
          <FormField
            control={form.control}
            name={names[2]}
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormControl>
                  <Input
                    placeholder={placeholders[2]}
                    type={type[2]}
                    step="any"
                    min="0"
                    className="rounded-l-none rounded-r-md focus:z-10 relative -ms-px"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      {hasErrors && (
        <FormMessage className='col-span-4 col-start-3'>
          {getErrorMessages().pop()}
        </FormMessage>
      )}
    </div>
  )
}
